import React, { useState, useMemo, useCallback } from 'react';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as InventorySelectors from 'selectors/InventorySelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { api } from 'common/frontend/api';
import { useSelector } from 'react-redux';
import { Box, Button, Grid, Theme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import DeleteSkuDialog from 'components/DeleteSkuDialog';
import { notUndefined } from 'common/utils/common';
import { useTranslation } from 'services/localization';
import InventorySearchBar from '../components/InventorySearchBar';
import SkuTable from './SkuTable/';
import AddSku from '../components/AddSku';
import { chain, includes } from 'lodash';
import { emptyInventoryFields } from 'common/modules/inventory';
import { hasHigherOrEqualAccess, getDisabledUserSkuIds } from 'common/modules/users/utils';
import InventoryLearnMore from 'components/InventoryLearnMore';
import { useQueryString } from 'common/hooks/useQueryString';

const SkuView = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId);
	const skuItems = useSelector(InventorySelectors.skuItems);
	const inventoryConfiguration = useSelector(InventorySelectors.inventoryConfiguration);
	const userLocations = useSelector(UserSelectors.userLocations);
	const userRoles = useSelector(UserSelectors.userRoles);
	const [deleteItemsLoading, setDeleteItemsLoading] = useState<boolean>(false);
	const [deleteItemsOpen, setDeleteItemsOpen] = useState<boolean>(false);
	const [addSkuOpen, setAddSkuOpen] = useState<boolean>(false);
	const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

	const [searchParams, setSearchParam] = useQueryString<{
		search: string;
		skuIds: string[];
		locationIds: string[];
	}>({
		locationIds: !!activeLocationId ? [activeLocationId] : undefined,
	});

	const selectedRowsCount = selectedRowIds.length;

	const selectedSkuArticlesCount = chain(skuItems.data ?? [])
		.filter(({ id }) => includes(selectedRowIds, id))
		.map(({ quantity }) => quantity)
		.sum()
		.value();

	const visibleSkuItems = useMemo(() => {
		const selectedSkuIds = searchParams.skuIds ?? [];
		if (!skuItems.data) return [];
		return selectedSkuIds.length
			? skuItems.data.filter((item) => selectedSkuIds.includes(item.id))
			: skuItems.data;
	}, [searchParams, skuItems.data]);

	const userCanAddSkus = hasHigherOrEqualAccess(userRoles, ['editor']);
	const disabledUserSkuIds = getDisabledUserSkuIds(visibleSkuItems, userLocations, userRoles);

	const handleSearchTextChange = useCallback(
		(searchText: string) => {
			setSearchParam('search', searchText);
		},
		[setSearchParam],
	);

	const handleSelectedSkuIdsChange = useCallback(
		(skuIds: string[]) => {
			setSearchParam('skuIds', skuIds);
		},
		[setSearchParam],
	);

	const handleSelectedLocationIdsChange = useCallback(
		(locationIds: string[]) => {
			setSearchParam('locationIds', locationIds);
		},
		[setSearchParam],
	);

	const deleteSelectedItems = async (opts: { deleteArticles: boolean }) => {
		setDeleteItemsLoading(true);
		const skuItemsToDelete = selectedRowIds
			.map((id) => {
				const skuItem = (skuItems.data ?? []).find((item) => item.id === id);
				return skuItem;
			})
			.filter(notUndefined);
		setSelectedRowIds([]);
		await Promise.all(skuItemsToDelete.map((item) => api().skuItems.doc(item.id).delete()));
		if (opts.deleteArticles) {
			await Promise.all(
				skuItemsToDelete
					.map((sku) => sku.id)
					.map(async (skuId) => {
						const allInventoryItems = await api()
							.inventoryItems.get.where('skuId', '==', skuId)
							.get();
						await Promise.all(
							allInventoryItems.map((i) => api().inventoryItems.doc(i.id).delete()),
						);
					}),
			);
		}
		setDeleteItemsOpen(false);
		setDeleteItemsLoading(false);
	};

	const openAddSkuModal = () => {
		setAddSkuOpen(true);
	};

	const closeAddSkuModal = () => {
		setAddSkuOpen(false);
	};

	return (
		<>
			<Box className={classes.container}>
				<Grid container justifyContent="space-between" alignItems="center" spacing={4}>
					<Grid item className={classes.btnOrderWrapper}>
						{!!selectedRowsCount ? (
							<Button variant="contained" color="primary" onClick={() => setDeleteItemsOpen(true)}>
								{t('inventory.deleteSelected', 'Delete selected')} ({selectedRowsCount})
							</Button>
						) : (
							<Button
								variant="contained"
								color="primary"
								disabled={!userCanAddSkus}
								onClick={openAddSkuModal}
							>
								{t('inventory.addSkus', `Add SKUs`)}
							</Button>
						)}
					</Grid>
					<Grid item xs={12} sm={true}>
						<InventorySearchBar
							searchValue={searchParams.search ?? ''}
							onSearchValueChange={handleSearchTextChange}
							selectedLocationIds={searchParams.locationIds}
							onSelectedLocationIdsChange={handleSelectedLocationIdsChange}
							skuItems={skuItems}
							selectedSkuIds={searchParams.skuIds}
							onSelectedSkuIdsChange={handleSelectedSkuIdsChange}
						/>
					</Grid>
				</Grid>
				<Box mt={2} />
				<SkuTable
					inventoryFields={inventoryConfiguration.data?.fields ?? emptyInventoryFields()}
					skuItems={visibleSkuItems}
					selectedRowIds={selectedRowIds}
					disabledRowIds={disabledUserSkuIds}
					setSelectedRowIds={setSelectedRowIds}
					searchValue={searchParams.search ?? ''}
					selectedLocationIds={searchParams.locationIds}
				/>
				<InventoryLearnMore />
			</Box>
			{deleteItemsOpen && (
				<DeleteSkuDialog
					open={deleteItemsOpen}
					skuCount={selectedRowsCount}
					articlesCount={selectedSkuArticlesCount}
					onClose={() => setDeleteItemsOpen(false)}
					handleDelete={deleteSelectedItems}
					loading={deleteItemsLoading}
				/>
			)}
			{addSkuOpen && (
				<AddSku
					open={addSkuOpen}
					onCancel={closeAddSkuModal}
					onCreate={closeAddSkuModal}
					skuItems={skuItems.data ?? []}
					shopId={shopId}
				/>
			)}
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		container: {
			flex: '1 1 auto',
			display: 'flex',
			flexDirection: 'column',
			padding: theme.spacing(4, 2, 7, 2),
			[theme.breakpoints.up('sm')]: {
				padding: theme.spacing(4, 4, 4, 4),
			},
			[theme.breakpoints.up('md')]: {
				padding: theme.spacing(4, 8, 4, 8),
			},
		},
		btnOrderWrapper: {
			order: 2,
			[theme.breakpoints.up('xs')]: {
				order: 1,
			},
		},
	}),
);

export default SkuView;
